/**
 * Renders a step's body by composing its resolved layout of primitives.
 *
 * This is the single seam that replaces the hard-coded `step.kind === '…' && <XStep/>` chain in
 * `StepPage`. It resolves the step's layout (authored primitives, or the coarse whole-body fallback —
 * see `resolveLayout`) and renders each primitive from the registry with the shared `RunnerContext`.
 * Because the fallback layout is the original body component with the original props, this renders
 * today's UI byte-for-byte until a finer layout is authored.
 */
import { Fragment, Suspense } from 'react';
import type { RunnerContext } from '../RunnerContext';
import { getPrimitive } from '../primitives/registry';
// Imported for its side effect: registers all built-in primitives into the registry.
import '../primitives/core';
import { resolveStepLayout } from './resolveLayout';

export function StepBodyRenderer({ ctx }: { ctx: RunnerContext }): JSX.Element {
  const layout = resolveStepLayout(ctx.step);
  return (
    // Some primitives are code-split (lazy) so their heavy deps stay out of the main bundle; one
    // Suspense boundary around the whole body covers them.
    <Suspense fallback={null}>
      {layout.map((entry, index) => {
        const Primitive = getPrimitive(entry.primitive);
        if (!Primitive) {
          // An authored layout referenced a primitive that isn't registered. Skip it rather than
          // crash the runner; surface it in development so the mis-keyed layout is caught.
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(
              `[guided] step "${ctx.step.slug}" layout references unknown primitive "${entry.primitive}"`,
            );
          }
          return null;
        }
        return (
          <Fragment key={`${entry.primitive}-${index}`}>
            <Primitive ctx={ctx} config={entry.config} />
          </Fragment>
        );
      })}
    </Suspense>
  );
}
